import React from 'react';
import { getFullName } from '../../../utils';
import { styledDefaultColDef, createAvatarNameRenderer } from '../../../extras/cellRenderers';

const getUserEmail = (data) => data?.email ?? '';

// ─── Name cell renderer with avatar ─────────────────────────────
const AvatarNameRenderer = createAvatarNameRenderer(
  (p) => p?.data?.user?.firstName,
  (p) => p?.data?.user?.lastName
);

function NameCellRenderer(props) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
      <AvatarNameRenderer {...props} />
    </div>
  );
}

// ─── Email cell renderer ────────────────────────────────────────
function EmailCellRenderer(props) {
  const email = getUserEmail(props.data?.user);
  return (
    <span style={{ fontSize: 13, color: '#374151' }}>
      {email || '—'}
    </span>
  );
}

// ─── User ID cell renderer (monospace badge) ────────────────────
function UserIdCellRenderer(props) {
  const userId = props.data?.userId;
  if (!userId) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;
  return (
    <span
      style={{
        fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
        fontSize: 11.5,
        fontWeight: 500,
        color: '#64748B',
        backgroundColor: '#F1F5F9',
        border: '1px solid #E2E8F0',
        borderRadius: 4,
        padding: '2px 6px',
        letterSpacing: '0.3px',
      }}
    >
      {userId}
    </span>
  );
}

// ─── Roles cell renderer (badge list) ───────────────────────────
function RolesCellRenderer(props) {
  const roles = props.data?.roles || [];
  if (!roles.length) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', height: '100%' }}>
      {roles.map((role, idx) => (
        <span
          key={idx}
          style={{
            display: 'inline-block',
            fontSize: 11,
            fontWeight: 500,
            color: '#4338CA',
            backgroundColor: '#EEF2FF',
            border: '1px solid #C7D2FE',
            borderRadius: 4,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          {role}
        </span>
      ))}
    </div>
  );
}

// ─── Course Type cell renderer ──────────────────────────────────
function CourseTypeCellRenderer(props) {
  const courseType = props.data?.courseType;
  if (!courseType) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;
  const formatted = courseType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: '#047857',
        backgroundColor: '#ECFDF5',
        border: '1px solid #A7F3D0',
        borderRadius: 4,
        padding: '2px 8px',
      }}
    >
      {formatted}
    </span>
  );
}

// ─── Cohort cell renderer ───────────────────────────────────────
function CohortCellRenderer(props) {
  const cohortName = props.data?.cohort?.name;
  if (!cohortName) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;
  return (
    <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
      {cohortName}
    </span>
  );
}

export const columnDefs = [
  {
    headerName: 'Name',
    field: 'name',
    filter: 'agTextColumnFilter',
    pinned: 'left',
    width: 220,
    valueGetter: (props) => getFullName(props.data.user),
    filterParams: {
      filterOptions: ['contains'],
      valueGetter: (props) => getFullName(props.data.user),
      debounceMs: 200,
    },
    cellRenderer: NameCellRenderer,
    comparator: (valueA, valueB, nodeA, nodeB) => {
      const nameA = getFullName(nodeA.data.user);
      const nameB = getFullName(nodeB.data.user);
      return nameA.localeCompare(nameB);
    },
  },
  {
    headerName: 'Email',
    field: 'email',
    filter: 'agTextColumnFilter',
    width: 220,
    valueGetter: (props) => getUserEmail(props?.data?.user),
    filterParams: {
      filterOptions: ['contains'],
      valueGetter: (props) => getUserEmail(props?.data?.user),
      debounceMs: 200,
    },
    cellRenderer: EmailCellRenderer,
    comparator: (valueA, valueB, nodeA, nodeB) => {
      const emailA = getUserEmail(nodeA.data.user);
      const emailB = getUserEmail(nodeB.data.user);
      return emailA.localeCompare(emailB);
    },
  },
  {
    headerName: 'User ID',
    field: 'userId',
    width: 140,
    cellRenderer: UserIdCellRenderer,
  },
  {
    headerName: 'Roles',
    field: 'roles',
    width: 280,
    cellRenderer: RolesCellRenderer,
  },
  {
    headerName: 'Course Type',
    field: 'courseType',
    width: 160,
    cellRenderer: CourseTypeCellRenderer,
  },
  {
    headerName: 'Cohort',
    field: 'cohort',
    width: 180,
    valueGetter: (props) => props.data?.cohort?.name,
    cellRenderer: CohortCellRenderer,
  },
  {
    headerName: 'Actions',
    field: 'actions',
    cellRenderer: 'btnCellRenderer',
    pinned: 'right',
    width: 240,
    sortable: false,
    suppressMenu: true,
  },
];

export const defaultColDef = {
  ...styledDefaultColDef,
  filter: true,
};
